import { flattenTree, type TreeNavNode, type TreeSource } from '@krp/ui-kit'

import type { CashRegisters } from './types'

/**
 * Дерево «Где смотрим» чеков 1С: организация → магазин или склад (по иерархии структурных единиц) → касса ККМ. Узел — это
 * набор касс: выбор узла отбирает чеки всех касс под ним. В дереве только те единицы, под которыми есть кассы, — сотни
 * складов без касс чеков не дают и дерево бы только раздували.
 */
export const ROOT_NODE = ''
/** Кассы, у которых организацию определить не удалось: отдельная группа, а не потеря. */
export const NO_ORGANIZATION = 'Без организации'
const NO_ORGANIZATION_ID = 'org:none'

export const organizationNode = (key: string) => `org:${key}`
export const unitNode = (key: string) => `unit:${key}`
export const registerNode = (key: string) => `reg:${key}`

const MAX_UNIT_DEPTH = 12

interface Branch {
  id: string
  label: string
  caption: string | null
  registers: string[]
  children: Map<string, Branch>
}

const branch = (id: string, label: string, caption: string | null = null): Branch => ({ id, label, caption, registers: [], children: new Map() })

const plural = (count: number, one: string, few: string, many: string): string => {
  const rest = count % 100
  if (rest >= 11 && rest <= 14) return many
  const last = count % 10
  return last === 1 ? one : last >= 2 && last <= 4 ? few : many
}

const registersText = (count: number) => `${count} ${plural(count, 'касса', 'кассы', 'касс')}`

const UNIT_CAPTION = { department: 'подразделение', warehouse: 'магазин или склад', other: null } as const

interface Built {
  roots: Branch[]
}

function build(directory: CashRegisters): Built {
  const orgNames = new Map(directory.organizations.map((item) => [item.key, item.name]))
  const units = new Map(directory.units.map((item) => [item.key, item]))
  const roots = new Map<string, Branch>()
  const sorted = [...directory.registers].sort((left, right) => left.name.localeCompare(right.name, 'ru'))

  for (const register of sorted) {
    const unit = register.unitKey === null ? undefined : units.get(register.unitKey)
    const organizationKey = register.organizationKey ?? unit?.organizationKey ?? null
    const organizationId = organizationKey !== null && orgNames.has(organizationKey) ? organizationNode(organizationKey) : NO_ORGANIZATION_ID
    let root = roots.get(organizationId)
    if (root === undefined) {
      root = branch(organizationId, organizationId === NO_ORGANIZATION_ID ? NO_ORGANIZATION : (orgNames.get(organizationKey ?? '') ?? NO_ORGANIZATION))
      roots.set(organizationId, root)
    }

    // Цепочка единиц сверху вниз: подразделение → склад или магазин; обрыв и цикл иерархии останавливают подъём.
    const chain: string[] = []
    const seen = new Set<string>()
    for (let current = unit; current !== undefined && !seen.has(current.key) && chain.length < MAX_UNIT_DEPTH; ) {
      seen.add(current.key)
      chain.unshift(current.key)
      current = current.parentKey === null ? undefined : units.get(current.parentKey)
    }

    let at = root
    root.registers.push(register.key)
    for (const unitKey of chain) {
      const found = units.get(unitKey)
      if (found === undefined) continue
      const id = unitNode(unitKey)
      let next = at.children.get(id)
      if (next === undefined) {
        next = branch(id, found.name, UNIT_CAPTION[found.kind])
        at.children.set(id, next)
      }
      next.registers.push(register.key)
      at = next
    }

    const leaf = branch(registerNode(register.key), register.code === null || register.code === '' ? register.name : `${register.name} (${register.code})`)
    leaf.registers.push(register.key)
    at.children.set(leaf.id, leaf)
  }

  // Группа «Без организации» — в конце: она про то, что не определилось, и не должна идти раньше настоящих организаций.
  const rank = (item: Branch) => (item.id === NO_ORGANIZATION_ID ? 1 : 0)
  return { roots: [...roots.values()].sort((left, right) => rank(left) - rank(right) || left.label.localeCompare(right.label, 'ru')) }
}

function sourceOf(item: Branch, counts: ReadonlyMap<string, number> | null): TreeSource {
  const leaf = item.id.startsWith('reg:')
  const children = [...item.children.values()]
  // Чеков у места за период: сумма по его кассам; чисел нет (чеки не прочитаны) — без числа.
  const badge = counts === null ? null : String([...new Set(item.registers)].reduce((total, key) => total + (counts.get(key) ?? 0), 0))
  const caption = leaf ? null : [item.caption, registersText(item.registers.length)].filter((part) => part !== null).join(' · ')
  return {
    id: item.id,
    label: item.label,
    caption,
    badge,
    ...(children.length > 0 ? { children: children.map((child) => sourceOf(child, counts)) } : {}),
  }
}

/** Источник дерева. Корень — вся база; единственная организация не прячется: человек видит, где стоит. */
export function treeSourceOf(directory: CashRegisters | undefined, counts: ReadonlyMap<string, number> | null = null): TreeSource[] {
  if (directory === undefined) return []
  const { roots } = build(directory)
  return [
    {
      id: ROOT_NODE,
      label: 'Вся база',
      caption: registersText(directory.registers.length),
      badge: counts === null ? null : String([...counts.values()].reduce((total, count) => total + count, 0)),
      children: roots.map((root) => sourceOf(root, counts)),
    },
  ]
}

export function treeNodesOf(
  directory: CashRegisters | undefined,
  collapsed: readonly string[],
  selectedId: string,
  counts: ReadonlyMap<string, number> | null = null,
): TreeNavNode[] {
  return flattenTree(treeSourceOf(directory, counts), collapsed, selectedId)
}

function findBranch(items: readonly Branch[], id: string, path: string[] = []): { branch: Branch; path: string[] } | undefined {
  for (const item of items) {
    const next = [...path, item.label]
    if (item.id === id) return { branch: item, path: next }
    const inner = findBranch([...item.children.values()], id, next)
    if (inner !== undefined) return inner
  }
  return undefined
}

/** Ключи касс под узлом; пусто — вся база (или узел не найден: отбор не должен молча стать пустым). */
export function registerKeysOf(directory: CashRegisters | undefined, nodeId: string): string[] {
  if (directory === undefined || nodeId === ROOT_NODE) return []
  const found = findBranch(build(directory).roots, nodeId)
  return found === undefined ? [] : [...new Set(found.branch.registers)].sort()
}

/** Узел существует в дереве. Выбранный узел, которого больше нет (кассу убрали в 1С), — это «вся база», а не пустота. */
export function nodeExists(directory: CashRegisters | undefined, nodeId: string): boolean {
  return nodeId === ROOT_NODE || (directory !== undefined && findBranch(build(directory).roots, nodeId) !== undefined)
}

/** Выбранное словами: «Вся база» или путь «ООО Ромашка · Магазин у дома · Касса зала». */
export function selectionText(directory: CashRegisters | undefined, nodeId: string): string {
  if (nodeId === ROOT_NODE || directory === undefined) return 'Вся база'
  const found = findBranch(build(directory).roots, nodeId)
  return found === undefined ? 'Вся база' : found.path.join(' · ')
}


/** Магазины, к которым привязаны кассы: один плоский список, те же ключи отбора, что у полного дерева. */
export function storeChoicesOf(directory: CashRegisters | undefined, counts: ReadonlyMap<string, number> | null = null): Pick<TreeNavNode, 'id' | 'label' | 'caption' | 'badge'>[] {
  if (directory === undefined) return []
  const { roots } = build(directory)
  const attached = new Set(directory.registers.map((item) => item.unitKey))
  const stores = directory.units.filter((item) => attached.has(item.key))
  const duplicateNames = new Map<string, number>()
  for (const store of stores) duplicateNames.set(store.name, (duplicateNames.get(store.name) ?? 0) + 1)
  return [
    { id: ROOT_NODE, label: 'Вся база', caption: 'Все магазины и кассы', badge: counts === null ? null : String([...counts.values()].reduce((total, value) => total + value, 0)) },
    ...stores.sort((left, right) => left.name.localeCompare(right.name, 'ru')).map((store) => {
      const found = findBranch(roots, unitNode(store.key))
      return { id: unitNode(store.key), label: store.name,
        caption: (duplicateNames.get(store.name) ?? 0) > 1 ? found?.path[0] ?? null : null,
        badge: counts === null ? null : String([...new Set(found?.branch.registers ?? [])].reduce((total, key) => total + (counts.get(key) ?? 0), 0)) }
    }),
  ]
}
